import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useConfirm } from 'Utils/ConfirmDialog';

/**
 * Name-prompt modal shared by the inventory flows: creating an inventory from discover results
 * (POST to the collection endpoint needs a name) and renaming an existing one. Controlled by the
 * host — this only collects a non-blank name and hands it back via onSubmit.
 */
export function InventoryNameModal({
  show,
  title = 'Name this inventory',
  initialName = '',
  submitLabel = 'Save',
  onSubmit,
  onCancel,
}) {
  const [name, setName] = useState(initialName);

  useEffect(() => {
    if (show) setName(initialName);
  }, [show, initialName]);

  const trimmed = String(name || '').trim();
  const submit = (e) => {
    if (e) e.preventDefault();
    if (trimmed) onSubmit(trimmed);
  };

  return (
    <Modal show={show} onHide={onCancel} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title as='h6'>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className='mb-0'>
            <Form.Label>Inventory name</Form.Label>
            <Form.Control
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. Production masking plan'
              data-testid='inventory-name-input'
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' onClick={onCancel}>
            Cancel
          </Button>
          <Button variant='primary' type='submit' disabled={!trimmed} data-testid='inventory-name-submit'>
            {submitLabel}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/**
 * Inventory picker for a data source under the multi-inventory model: a dropdown of the source's
 * named inventories plus New / Rename / Delete actions. The host owns all persistence:
 *   - onSelect(id)   — user picked an existing inventory (id is a number)
 *   - onNew()        — user wants a fresh unsaved draft (host shows it as selectedId == null;
 *                      the actual POST happens on the host's Save with a name prompt)
 *   - onRename(name) — rename the selected inventory (PUT { name })
 *   - onDelete()     — delete the selected inventory (host confirms refresh/selection)
 * Rename/Delete are hidden while the draft (selectedId == null) is active.
 */
function InventorySelector({
  inventories,
  selectedId,
  onSelect,
  onNew,
  onRename,
  onDelete,
  disabled = false,
  label = 'Inventory',
  /** Optional option-label override, e.g. to suffix target-derived inventories with their target. */
  describe,
}) {
  const { confirm, confirmDialog } = useConfirm();
  const [renameOpen, setRenameOpen] = useState(false);
  const list = (inventories || []).filter(Boolean);
  const selected = selectedId != null ? list.find((inv) => String(inv.id) === String(selectedId)) : null;
  const isDraft = selectedId == null;

  const handleDelete = async () => {
    if (!selected) return;
    const name = selected.name || `Inventory ${selected.id}`;
    if (await confirm({ title: `Delete inventory "${name}"?`, body: 'This cannot be undone.', confirmLabel: 'Delete', variant: 'danger' })) {
      onDelete();
    }
  };

  return (
    <Form.Group className='mb-0 d-flex align-items-center' style={{ gap: '0.4rem' }}>
      {confirmDialog}
      <Form.Label className='mb-0 text-nowrap' style={{ fontWeight: 500 }}>
        {label}
      </Form.Label>
      <Form.Control
        as='select'
        size='sm'
        data-testid='inventory-select'
        value={isDraft ? '' : String(selectedId)}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') {
            if (onNew) onNew();
          } else {
            onSelect(Number(v));
          }
        }}
        style={{ width: 'auto', minWidth: 160 }}
      >
        {(isDraft || onNew) && <option value=''>{isDraft ? 'New inventory (unsaved)' : 'New inventory…'}</option>}
        {list.map((inv) => (
          <option key={inv.id} value={String(inv.id)}>
            {(typeof describe === 'function' && describe(inv)) || inv.name || `Inventory ${inv.id}`}
          </option>
        ))}
      </Form.Control>
      {!isDraft && onRename && (
        <Button
          size='sm'
          variant='outline-secondary'
          disabled={disabled || !selected}
          onClick={() => setRenameOpen(true)}
          data-testid='inventory-rename'
        >
          Rename
        </Button>
      )}
      {!isDraft && onDelete && (
        <Button
          size='sm'
          variant='outline-danger'
          disabled={disabled || !selected}
          onClick={handleDelete}
          data-testid='inventory-delete'
        >
          Delete
        </Button>
      )}
      <InventoryNameModal
        show={renameOpen}
        title='Rename inventory'
        initialName={selected ? selected.name || '' : ''}
        submitLabel='Rename'
        onSubmit={(name) => {
          setRenameOpen(false);
          onRename(name);
        }}
        onCancel={() => setRenameOpen(false)}
      />
    </Form.Group>
  );
}

export default InventorySelector;
